export interface BudgetDepartment {
  id: number;
  dept_code: string;
  dept_name: string;
  fiscal_year: string;
  drive_folder_id?: string;
}

export interface BudgetItemFinancial {
  id: number;
  item_id: number;
  fiscal_year_label: string;
  column_type: string;
  fund_type: string;
  amount: number;
}

export interface BudgetItemDocument {
  id: number;
  item_id?: number;
  dept_id?: number;
  doc_type: string;
  doc_title: string;
  doc_url: string;
  published_date?: string;
  source?: string;
  is_primary_source: boolean;
}

export interface BudgetItemBill {
  id: number;
  budget_item_id: number;
  bill_id: number;
  bill_number: string;
  bill_title: string;
  bill_session?: string;
  relation_type?: string;
  bill_category?: string;
  is_placeholder?: boolean;
  placeholder_note?: string;
}

export interface BudgetItemWorkspace {
  workspace_id: string;
  workspace_name: string;
  position?: string;
  is_hot: boolean;
  needs_client_review: boolean;
  tracked_by?: string;
}

export interface BudgetItem {
  id: number;
  dept_id: number;
  dept_code: string;
  dept_name: string;
  item_number: string;
  priority_rank?: number;
  item_title: string;
  fiscal_year: string;
  status: string;
  requires_legislation: boolean;
  is_oit_shared: boolean;
  oit_program_key?: string;
  request_type?: string;
  governor_request_key?: string;
  source_file_url?: string;
  source_file_type?: string;
  tags?: string[];
  predecessor_item_id?: number;
  created_at: string;
  updated_at: string;

  // Detail-only fields
  financials?: BudgetItemFinancial[];
  documents?: BudgetItemDocument[];
  relatedBills?: BudgetItemBill[];
  workspaces?: BudgetItemWorkspace[];
  drive_folder_id?: string;

  // Workspace tracking fields (present when workspace_id filter is used)
  tracked_position?: string;
  tracked_is_hot?: boolean;
  tracked_review?: boolean;
  is_tracked?: boolean;

  // List-level aggregated fields (from lateral subqueries)
  total_amount?: number;
  financials_detail?: { fund_type: string; amount: number }[];
  linked_bills?: { bill_id: number; number: string; title: string; bill_category?: string }[];

  // Workspace tab fields (from tracking list endpoint)
  position?: string;
  isHot?: boolean;
  needsClientReview?: boolean;
  trackedBy?: string;
  itemNumber?: string;
  itemTitle?: string;
  fiscalYear?: string;
  deptCode?: string;
  deptName?: string;
}

export interface BudgetHistoryItem {
  id: number;
  item_number: string;
  item_title: string;
  fiscal_year: string;
  status: string;
  predecessor_item_id?: number;
  dept_code: string;
  dept_name: string;
  depth: number;
}

export interface BudgetProcessPhase {
  id: number;
  fiscal_year: string;
  phase_key: string;
  phase_name: string;
  phase_description?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  status: string;
  derivedStatus?: string;
  sort_order: number;
  notes?: string;
}

export interface BudgetNarrativeSection {
  id: number;
  dept_id?: number;
  dept_code?: string;
  dept_name?: string;
  fiscal_year: string;
  section_type: string;
  section_title: string;
  section_order: number;
  raw_content?: string;
  ai_explanation?: string;
  structured_data?: Record<string, unknown>;
  source_document_id?: number;
  page_range?: string;
}

export interface BudgetBillImpact {
  id: number;
  bill_id: number;
  fiscal_year_label: string;
  impact_type: string;
  fund_type: string;
  amount: number;
  notes?: string;
}

export interface BudgetBillProfile {
  items: BudgetItemBill[];
  impacts: BudgetBillImpact[];
  departments: { dept_code: string; dept_name: string }[];
  billCategory?: string;
  isPlaceholder: boolean;
}

export interface DepartmentOverview {
  department: BudgetDepartment;
  fiscalYear: string;
  fundingSummary: { fund_type: string; total_amount: number }[];
  items: BudgetItem[];
  companionBills: BudgetItemBill[];
  itemCount: number;
  billCount: number;
}

export type BudgetDisplayContext = 'budget' | 'workspace';

export interface BudgetFetchParams {
  search?: string;
  dept_code?: string;
  fiscal_year?: string;
  status?: string;
  min_amount?: number;
  max_amount?: number;
  fund_type?: string;
  bill_number?: string;
  tags?: string;
  has_fte_changes?: string;
  bill_category?: string;
  workspace_id?: string;
  tracked_only?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface BudgetItemsResponse {
  success: boolean;
  data: {
    items: BudgetItem[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    currentPage: number;
    totalPages: number;
  };
}

export interface BudgetFilterOptions {
  fiscalYears: string[];
  departments: { dept_code: string; dept_name: string }[];
  statuses: string[];
  tags: string[];
}

export interface WorkspacePermissions {
  canView: boolean;
  canEdit: boolean;
  canDelete?: boolean;
  canManage?: boolean;
}

export const BUDGET_STATUSES = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'figure_set', label: 'Figure Set' },
  { value: 'approved', label: 'Approved' },
  { value: 'reduced', label: 'Reduced' },
  { value: 'denied', label: 'Denied' },
] as const;

export type BudgetStatus = typeof BUDGET_STATUSES[number]['value'];

export const BUDGET_POSITIONS = [
  { value: 'Monitor', label: 'Monitor' },
  { value: 'Support', label: 'Support' },
  { value: 'StrSupport', label: 'Strong Support' },
  { value: 'Neutral', label: 'Neutral' },
  { value: 'Oppose', label: 'Oppose' },
  { value: 'StrOppose', label: 'Strong Oppose' },
  { value: 'Amend', label: 'Amend' },
  { value: 'Pending', label: 'Pending' },
] as const;

export type BudgetPosition = typeof BUDGET_POSITIONS[number]['value'];

export const FUND_TYPES = [
  { value: 'GF', label: 'General Fund' },
  { value: 'CF', label: 'Cash Funds' },
  { value: 'RF', label: 'Reappropriated' },
  { value: 'FF', label: 'Federal Funds' },
  { value: 'FTE', label: 'FTE' },
  { value: 'Total', label: 'Total' },
] as const;

export interface DepartmentInitiative {
  id: number;
  dept_id: number;
  fiscal_year: string;
  initiative_text: string;
  sort_order: number;
  created_by?: number;
  created_at: string;
  updated_at: string;
}

export interface DepartmentSummary {
  department: BudgetDepartment;
  fundingSummary: { fund_type: string; total_amount: number }[];
  totalAmount: number;
  itemCount: number;
  documentCount: number;
  initiatives: DepartmentInitiative[];
}

export const BILL_CATEGORIES = [
  { value: 'jbc_package', label: 'JBC Package' },
  { value: 'companion', label: 'Companion Bill' },
  { value: 'placeholder', label: 'Placeholder' },
  { value: 'balancing_assumption', label: 'Balancing Assumption' },
] as const;
